import { useState } from "react";

import { DEFAULT_KCAL_GOAL, DEFAULT_MOVE_GOAL, DEFAULT_STEP_GOAL } from "../../domain/activity";
import {
  defaultDeficit,
  defaultProteinPerKg,
  deficitSetting,
  maxDeficitSetting,
  proteinSetting,
  proteinSettingRange,
} from "../../domain/eating";
import { dinnerSettings } from "../../domain/dinnerNudge";
import { MAX_PREP_SECS, prepSecs } from "../../domain/queue";
import type { KayaState, Profile as ProfileT } from "../../domain/types";
import { askNotificationPermission, notificationPermission } from "../../lib/device";
import { scheduleReminders } from "../../lib/reminders";
import { useKaya } from "../../store/kayaStore";
import { setCycleSettings, setDinnerReminder, setPeriodStart } from "../../store/toggles";

/**
 * One form shape shared by every settings screen.
 *
 * The screens each render a *slice* of it, but they all seed from the whole profile and save the
 * whole profile. That is deliberate: the alternative is eight partial save functions, each able to
 * drop a field it forgot about. Seeding from current state means an untouched field round-trips
 * unchanged, so a screen can only alter what it actually shows.
 *
 * Every number is held as a string so a box can be emptied while it is being retyped.
 */
export interface Form {
  name: string;
  heightCm: string;
  birthYear: string;
  stepGoal: string;
  kcalGoal: string;
  moveMinGoal: string;
  startWeight: string;
  goal: ProfileT["goal"];
  targetWeight: string;
  targetWaist: string;
  startDate: string;
  phaseMode: string;
  sex: string;
  cycleOn: boolean;
  periodsStopped: boolean;
  lastStart: string;
  cycleLen: string;
  periodLen: string;
  irregular: boolean;
  hipSafe: boolean;
  hasTreadmill: boolean;
  foodOn: boolean;
  deficitKcal: string;
  proteinPerKg: string;
  restMove: string;
  restRound: string;
  prepSecs: string;
  brisk1: string;
  easy1: string;
  brisk: string;
  easy: string;
  incline: string;
  sound: boolean;
  vibrate: boolean;
  notifyWanted: boolean;
  remindOn: boolean;
  remindMorning: string;
  remindEvening: string;
  dinnerRemindOn: boolean;
  dinnerRemindAt: string;
}

/** `+value || fallback` from the original: a blank or unparseable box keeps the old number. */
export function numOr(s: string, fallback: number): number {
  return Number(s) || fallback;
}

export function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

export function seed(S: KayaState): Form {
  const p = S.profile;
  return {
    name: p.name,
    heightCm: String(p.heightCm),
    birthYear: p.birthYear ? String(p.birthYear) : "",
    stepGoal: String(p.stepGoal ?? DEFAULT_STEP_GOAL),
    kcalGoal: String(p.kcalGoal ?? DEFAULT_KCAL_GOAL),
    moveMinGoal: String(p.moveMinGoal ?? DEFAULT_MOVE_GOAL),
    startWeight: String(p.startWeight),
    goal: p.goal,
    targetWeight: p.targetWeight ? String(p.targetWeight) : "",
    targetWaist: p.targetWaist ? String(p.targetWaist) : "",
    startDate: p.startDate,
    phaseMode: p.phaseMode,
    sex: p.sex,
    cycleOn: p.cycleOn,
    periodsStopped: p.periodsStopped === true,
    lastStart: S.cycle.starts.slice().sort().pop() || "",
    cycleLen: String(S.cycle.length || 28),
    periodLen: String(S.cycle.periodLen || 5),
    irregular: p.irregular,
    hipSafe: p.hipSafe,
    hasTreadmill: p.hasTreadmill !== false,
    foodOn: p.foodOn !== false,
    deficitKcal: String(deficitSetting(S)),
    proteinPerKg: String(proteinSetting(S)),
    restMove: String(p.restMove || 0),
    restRound: String(p.restRound || 0),
    prepSecs: String(prepSecs(S)),
    brisk1: String(p.brisk1),
    easy1: String(p.easy1),
    brisk: String(p.brisk),
    easy: String(p.easy),
    incline: String(p.incline),
    sound: p.sound,
    vibrate: p.vibrate,
    notifyWanted: p.notifyWanted || p.notify,
    remindOn: p.remindOn,
    remindMorning: p.remindMorning,
    remindEvening: p.remindEvening,
    dinnerRemindOn: dinnerSettings(S).on,
    dinnerRemindAt: dinnerSettings(S).at,
  };
}

/**
 * Writes the form into a draft.
 *
 * `hasTreadmill` and `foodOn` are written only when their own box was touched: both seed as "yes"
 * when unset, and writing the seed back on, say, a Body & goals save would record an answer
 * nobody gave — the setup checklist read exactly that as "has a treadmill". The last period start
 * goes through `setPeriodStart` so it out-stamps an earlier removal of the same date (ADR-019).
 */
export function applyForm(
  d: KayaState,
  f: Form,
  touched: ReadonlySet<keyof Form>,
  granted: boolean,
  now: number = Date.now()
): void {
  const q = d.profile;
  q.name = f.name.trim();
  q.heightCm = numOr(f.heightCm, q.heightCm);
  q.stepGoal = clamp(numOr(f.stepGoal, DEFAULT_STEP_GOAL), 500, 50000);
  q.kcalGoal = clamp(numOr(f.kcalGoal, DEFAULT_KCAL_GOAL), 50, 3000);
  q.moveMinGoal = clamp(numOr(f.moveMinGoal, DEFAULT_MOVE_GOAL), 5, 300);
  q.startWeight = numOr(f.startWeight, q.startWeight);
  q.goal = f.goal;
  if (f.goal === "weight") q.targetWeight = f.targetWeight ? Number(f.targetWeight) : 0;
  else q.targetWaist = f.targetWaist ? Number(f.targetWaist) : 0;
  q.startDate = f.startDate || q.startDate;
  q.phaseMode = f.phaseMode;
  q.hipSafe = f.hipSafe;
  if (touched.has("hasTreadmill")) q.hasTreadmill = f.hasTreadmill;
  if (touched.has("foodOn")) q.foodOn = f.foodOn;
  // A year rather than an age, so it cannot go stale — and cleared rather than kept at a
  // nonsense value, so the cards that need it say what is missing instead of lying. Written
  // before the deficit and protein, whose limits follow the age this save records (ADR-039).
  const by = Math.round(numOr(f.birthYear, 0));
  if (by >= 1900 && by <= new Date(now).getFullYear() - 5) q.birthYear = by;
  else delete q.birthYear;

  const at = new Date(now);
  const range = proteinSettingRange(d, at);
  // 0 is a real deficit (maintenance), so a blank box alone falls back to the default.
  const deficit = f.deficitKcal.trim() === "" ? NaN : Number(f.deficitKcal);
  q.deficitKcal = clamp(Number.isFinite(deficit) ? Math.round(deficit) : defaultDeficit(d, at), 0, maxDeficitSetting(d, at));
  q.proteinPerKg = clamp(numOr(f.proteinPerKg, defaultProteinPerKg(d, at)), range.min, range.max);

  q.sex = f.sex;
  // Stopped periods retire tracking in the same write, so a phone on a build that predates the
  // switch reads tracking as off too (ADR-039).
  q.periodsStopped = f.sex === "female" && f.periodsStopped;
  q.cycleOn = f.cycleOn && !q.periodsStopped;
  if (q.cycleOn) {
    q.irregular = f.irregular;
    if (f.lastStart && !d.cycle.starts.includes(f.lastStart)) setPeriodStart(d, f.lastStart, true, now);
    // Through the setter so the settings block carries its stamp and survives a merge (ADR-033).
    setCycleSettings(
      d,
      { length: clamp(numOr(f.cycleLen, 28), 20, 45), periodLen: clamp(numOr(f.periodLen, 5), 2, 8) },
      now
    );
  }

  q.brisk1 = numOr(f.brisk1, q.brisk1);
  q.easy1 = numOr(f.easy1, q.easy1);
  q.brisk = numOr(f.brisk, q.brisk);
  q.easy = numOr(f.easy, q.easy);
  q.incline = numOr(f.incline, 0);
  q.restMove = Math.max(0, numOr(f.restMove, 0));
  q.restRound = Math.max(0, numOr(f.restRound, 0));
  // 0 is a real choice here, so numOr's "blank keeps the old value" would swallow it.
  q.prepSecs = clamp(Math.round(Number(f.prepSecs) || 0), 0, MAX_PREP_SECS);

  q.sound = f.sound;
  q.vibrate = f.vibrate;
  q.notifyWanted = f.notifyWanted;
  q.notify = f.notifyWanted && granted;

  q.remindOn = f.remindOn;
  q.remindMorning = f.remindMorning || q.remindMorning;
  q.remindEvening = f.remindEvening || q.remindEvening;
  // Only when touched, like the treadmill: the seed is "off at 21:00", and writing it back on an
  // unrelated save would put an answer nobody gave into every profile.
  // Through the setter, so the pair carries the stamp a linked account's change is weighed
  // against (ADR-046).
  setDinnerReminder(
    d,
    {
      on: touched.has("dinnerRemindOn") ? f.dinnerRemindOn : undefined,
      at: touched.has("dinnerRemindAt") && f.dinnerRemindAt ? f.dinnerRemindAt : undefined,
    },
    now
  );
}

export interface ProfileForm {
  f: Form;
  set: <K extends keyof Form>(k: K, v: Form[K]) => void;
  save: () => Promise<void>;
  /** true once a box has been touched — drives the "unsaved" state on the save button */
  dirty: boolean;
}

export function useProfileForm(): ProfileForm {
  const { S, update, showToast } = useKaya();
  const [f, setF] = useState<Form>(() => seed(S));
  const [dirty, setDirty] = useState(false);
  const [touched, setTouched] = useState<ReadonlySet<keyof Form>>(() => new Set());

  const set = <K extends keyof Form>(k: K, v: Form[K]): void => {
    setDirty(true);
    setTouched((t) => (t.has(k) ? t : new Set([...t, k])));
    setF((x) => {
      const next: Form = { ...x };
      next[k] = v;
      return next;
    });
  };

  async function save(): Promise<void> {
    let granted = notificationPermission() === "granted";
    // Turning Log dinner? on asks too, as water's switch does: a nudge nobody may show is no nudge.
    const wantsDinner = touched.has("dinnerRemindOn") && f.dinnerRemindOn;
    if ((f.notifyWanted || wantsDinner) && !granted) {
      const r = await askNotificationPermission();
      granted = r === "granted";
      if (!granted)
        showToast(
          r === "denied"
            ? "Blocked by the browser — see the note under Alerts"
            : r === "unsupported"
              ? "Notifications aren't supported here"
              : "Permission not granted"
        );
    }

    update((d) => applyForm(d, f, touched, granted));

    scheduleReminders(useKaya.getState().S);
    setDirty(false);
    setTouched(new Set());
    showToast("Saved");
  }

  return { f, set, save, dirty };
}
